import { gql } from 'apollo-angular';
import DocSubtype from '../../../interfaces/doc-subtype';
import Document from '../../../interfaces/document';
import DocType from '../../../interfaces/doc-type';
import DocGroup from '../../../interfaces/doc-group';
import TaxReturnDifficultyLevels from '../../../interfaces/tax-return-difficulty-levels';
import DocTypeSubtype from '../../../interfaces/doc-type-subtype';
import { Paginator, PaginatorInfo } from '../../../types/paginator';
import DocStatus from '../../../interfaces/doc-status';
import ID from '../../../types/id';
import VDocumentsFilteringTable from '../../../interfaces/v-documents-filtering-table';
import DocumentSubtypeDueDate from '../../../interfaces/document-subtype-due-date';
import DateInput from '../../../types/date-input';
import SqlOperator from '../../../enums/sql-operator';
import { EventInputTransformer } from '@fullcalendar/core';
import OwnedEntity from '../../../interfaces/owned-entity';
import VOwnedEntitiesMap from '../../../interfaces/v-owned-entities-map';
import { Direction } from '../../../types/relation-order-by';

export type DocSorting = {
  column?: string;
  order: Direction;
};
export enum WhereColumn {
  FUND_NAME = 'FUND_NAME',
  OWNED_ENTITY_NAME = 'OWNED_ENTITY_NAME',
  PROJECT_NAME = 'PROJECT_NAME',
}
export type WhereConditions = {
  OR?: WhereConditions[];
  column?: WhereColumn;
  operator?: SqlOperator;
  value?: any;
};

export type DocumentFilter = {
  doc_date?: DateInput;
  due_date?: DateInput;
  current_status_id?: ID[];
  current_doc_subtype_id?: ID[];
  doc_type_id?: ID[];
  doc_group_id?: ID[];
  asset_manager_name?: string;
  auditor_name?: string;
  tax_return_difficulty_level_id?: ID[];
};
type DocumentFetchVariables = {
  order: DocSorting[];
  where: WhereConditions | null;
  filter: DocumentFilter;
};
type DocumentFetchResult = {
  vDocumentsFilterTable: Paginator<VDocumentsFilteringTable, PaginatorInfo>;
  docTypes: DocType[];
  docGroups: DocGroup[];
  docSubtypes: DocSubtype[];
  docTypeSubtypes: DocTypeSubtype[];
  docStatuses: DocStatus[];
  taxReturnDifficultyLevels: TaxReturnDifficultyLevels[];
};

const documentFields = `
    id
    doc_type_id
    doc_date
    current_doc_subtype_id
    current_doc_status_id
    current_version
    current_status_date
    user{
      id
      person{
        id
        name
      }
    }
`;
const documentFilterFields = `
    id
    document {
      ${documentFields}
    }
    owned_entity_id
    doc_type_id
    doc_date
    current_doc_subtype_id
    current_doc_status_id
    due_date_id
    due_date
    project_id
    fund_id
    owned_entity_name
    project_name
    fund_name
    tax_return_difficulty_level_id
    tax_return_difficulty_level
    expected_date
    last_followup_sent
    assess_fees
    asset_manager_id
    asset_manager_name
    auditor_name
    auditor_id
    created
    modified
    note_text
    document_version_id
`;
export const FETCH_DOCUMENTS = gql<DocumentFetchResult, DocumentFetchVariables>`
  query getDocuments($page: Int, $filter:DocumentFilter, $where:QueryVDocumentsFilterTableWhereWhereConditions, $order: [QueryVDocumentsFilterTableOrderOrderByClause!]) {
    vDocumentsFilterTable(page: $page, first:50, order:$order, where: $where, filter: $filter){
      data{
        ${documentFilterFields}
      }

      paginatorInfo {
        currentPage
        total
        perPage
        hasMorePages
      }
    }
    docTypes {
      id
      name
      permissions{
        _read
        _edit
        _delete
      }
      docSubtypes {
        id
        name
      }
      docTypeSubtypeStatuses {
        id
        docStatus{
          id
          name
        }
        doc_status_id
        sort_order
        doc_subtype_id
      }
    }
    docStatuses {
      id
      name
    }
    docSubtypes {
      id
      name
      sort_order
    }
    docGroups {
      id
      name
    }
    docTypeSubtypes {
      id
      doc_type_id
      doc_subtype_id
      sort_order
    }
    taxReturnDifficultyLevels {
      id,
      difficulty_level
    }
    ownedEntityTypes {
        id
        name
    }
  }
`;
type FetchDocumentResult = {
  vDocumentsFilter: VDocumentsFilteringTable;
};
type FetchDocumentVariables = {
  id: ID;
};
export const FETCH_DOCUMENT = gql<FetchDocumentResult, FetchDocumentVariables>`
    query fetchDocument($id:ID!){
       vDocumentsFilter(document_id:$id){
        ${documentFilterFields}
       }
    }
`;

export type DocumentRefreshResults = {
  vDocumentsFilterTable: Paginator<VDocumentsFilteringTable, PaginatorInfo>;
};
export type DocumentRefreshVariables = {
  page: number;
  filter?: DocumentFilter | null;
  where: WhereConditions | null;
  order: DocSorting[];
};
export const REFRESH_DOCUMENTS = gql<
  DocumentRefreshResults,
  DocumentRefreshVariables
>`
    query refreshDocuments($page:Int, $where:QueryVDocumentsFilterTableWhereWhereConditions, $filter: DocumentFilter, $order: [QueryVDocumentsFilterTableOrderOrderByClause!]){
      vDocumentsFilterTable(page:$page, where: $where, filter:$filter, first:50, order: $order){
        data{
            ${documentFilterFields}
        }
        paginatorInfo {
          currentPage
          total
          hasMorePages
          perPage
        }
      }
    }
`;

export type UpdateDocumentInput = {
  current_version?: number;
  current_doc_status_id?: ID;
  user_id?: ID;
  last_followup_sent?: string | null;
  current_status_date?: string | null;
  expected_date?: string | null;
  assess_fees?: boolean;
};
type UpdateDocumentVariables = {
  id: ID;
  input: UpdateDocumentInput;
};
type UpdateDocumentResult = {
  updateDocument: Document;
};
export const UPDATE_DOCUMENT = gql<
  UpdateDocumentResult,
  UpdateDocumentVariables
>`
    mutation updateDocument($id: ID, $input: UpdateDocumentInput){
        updateDocument(id: $id, input:$input){
            ${documentFields}
        }
    }
`;

type UpdateDueDateResult = {
  updateDocDueDate: DocumentSubtypeDueDate;
};
export type UpdateDueDateVariables = {
  doc_subtype_id: ID | null;
  document_id: ID;
  due_date: string;
  user_id: ID | null;
};
export const UPDATE_DUE_DATE = gql<UpdateDueDateResult, UpdateDueDateVariables>`
  mutation updateDocumentDueDate(
    $doc_subtype_id: ID
    $document_id: ID
    $due_date: Date
    $user_id: ID
  ) {
    updateDocDueDate(
      doc_subtype_id: $doc_subtype_id
      document_id: $document_id
      due_date: $due_date
    ) {
      id
      document_id
    }
    updateDocument(id: $document_id, input: { user_id: $user_id }) {
      id
    }
  }
`;

export type DeleteDocumentResults = {
  deleteDocument: Document;
};
export type DeleteDocumentVariables = {
  id: ID;
};
export const DELETE_DOCUMENT = gql<
  DeleteDocumentResults,
  DeleteDocumentVariables
>`
  mutation deleteDocument($id: ID!) {
    deleteDocument(id: $id) {
      id
    }
  }
`;

type EntityWhereConditions = {
  AND?: EntityWhereConditions[];
  OR?: EntityWhereConditions[];
  column?: 'OWNED_ENTITY_NAME' | 'RELATED_NAME' | 'TYPE';
  operator?: SqlOperator;
  value?: any;
};

type SearchOwnedEntitiesResults = {
  vOwnedEntityMaps: VOwnedEntitiesMap[];
};
export type SearchOwnedEntitiesVariables = {
  where: EntityWhereConditions;
};

export const SEARCH_ENTITIES = gql<
  SearchOwnedEntitiesResults,
  SearchOwnedEntitiesVariables
>`
  query SearchOwnedEntities($where: QueryVOwnedEntityMapsWhereWhereConditions) {
    vOwnedEntityMaps(where: $where) {
      id
      owned_entity_id
      owned_entity_name
      related_id
      related_name
      type
    }
  }
`;

export type CreateDocumentInput = {
  current_version: number;
  current_doc_status_id: ID;
  current_doc_subtype_id: ID;
  doc_type_id: ID;
  user_id: ID;
  doc_date: string;
  owned_entity_id: ID;
  current_status_date: string;
};
type CreateDocumentResults = {
  createDocument: Document;
};
type CreateDocumentVariables = {
  input: CreateDocumentInput;
};
export const CREATE_DOCUMENT = gql<
  CreateDocumentResults,
  CreateDocumentVariables
>`
  mutation createDocument($input: CreateDocumentInput) {
    createDocument(input: $input) {
      id
    }
  }
`;
